import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock, Loader2 } from "lucide-react";

type Schedule = {
  schedule_enabled: boolean;
  schedule_frequency: "daily" | "weekly";
  schedule_hour: number;
  schedule_weekday: number;
  schedule_last_run_at: string | null;
  schedule_next_run_at: string | null;
};

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function SecurityScheduleSettingsPanel() {
  const [cfg, setCfg] = useState<Schedule | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await supabase
      .from("security_alert_settings")
      .select(
        "schedule_enabled, schedule_frequency, schedule_hour, schedule_weekday, schedule_last_run_at, schedule_next_run_at",
      )
      .eq("id", true)
      .maybeSingle();
    if (data) setCfg(data as Schedule);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    const { error } = await supabase
      .from("security_alert_settings")
      .update({
        schedule_enabled: cfg.schedule_enabled,
        schedule_frequency: cfg.schedule_frequency,
        schedule_hour: cfg.schedule_hour,
        schedule_weekday: cfg.schedule_weekday,
        updated_at: new Date().toISOString(),
      })
      .eq("id", true);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Scheduled rescans updated");
    load();
  };

  if (!cfg) {
    return (
      <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
        Loading schedule…
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <CalendarClock className="size-4 text-primary" />
            Scheduled security rescans
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            An automated job checks hourly and queues a rescan when the schedule is due. New findings
            trigger the admin alerts configured below and appear as a dashboard warning.
          </p>
        </div>
        <Switch
          checked={cfg.schedule_enabled}
          onCheckedChange={(v) => setCfg({ ...cfg, schedule_enabled: v })}
          aria-label="Enable scheduled rescans"
        />
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="font-mono text-[10px] uppercase text-muted-foreground">Frequency</label>
          <Select
            value={cfg.schedule_frequency}
            onValueChange={(v) => setCfg({ ...cfg, schedule_frequency: v as "daily" | "weekly" })}
          >
            <SelectTrigger className="h-9 mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase text-muted-foreground">Hour (UTC)</label>
          <Input
            type="number"
            min={0}
            max={23}
            value={cfg.schedule_hour}
            onChange={(e) =>
              setCfg({ ...cfg, schedule_hour: Math.max(0, Math.min(23, Number(e.target.value) || 0)) })
            }
            className="h-9 mt-1"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase text-muted-foreground">Weekday</label>
          <Select
            value={String(cfg.schedule_weekday)}
            disabled={cfg.schedule_frequency !== "weekly"}
            onValueChange={(v) => setCfg({ ...cfg, schedule_weekday: Number(v) })}
          >
            <SelectTrigger className="h-9 mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WEEKDAYS.map((d, i) => (
                <SelectItem key={d} value={String(i)}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
        <div className="font-mono text-[10px] text-muted-foreground">
          last run {cfg.schedule_last_run_at ? new Date(cfg.schedule_last_run_at).toLocaleString() : "—"} · next{" "}
          {cfg.schedule_next_run_at ? new Date(cfg.schedule_next_run_at).toLocaleString() : "on next due hour"}
        </div>
        <Button size="sm" onClick={save} disabled={saving}>
          {saving && <Loader2 className="size-4 animate-spin mr-1" />}
          Save schedule
        </Button>
      </div>
    </div>
  );
}
